/** @jsxImportSource @emotion/react */
import { css, SerializedStyles } from "@emotion/react"
import React, { useEffect, useMemo, useRef } from "react"
import { FieldValues, UseControllerProps, useController } from "react-hook-form"

import { capitalizeFirstLetter } from "../../../../../utils"
import { ErrorIconWithTooltip, Select } from "../../../../components"
import { WaveSelectContainer } from "./WaveSelect.styled"

interface IWaveSelectProps<T extends FieldValues> extends UseControllerProps<T> {
  optionsEnum: Record<string, string>
  onChange?: () => void
  disabled?: boolean
  customCss?: SerializedStyles
}

export const WaveSelect = <T extends FieldValues>({
  optionsEnum,
  control,
  name,
  onChange,
  rules,
  disabled,
  customCss,
}: IWaveSelectProps<T>) => {
  const isFirstMount = useRef(true)

  const { field, fieldState } = useController({ control, name, rules })

  /**
   * Create select options from the enum
   */
  const options = useMemo(
    () =>
      Object.values(optionsEnum).map(value => ({
        label: capitalizeFirstLetter(value.replace(/_/g, " ")),
        value: value,
      })),
    [optionsEnum],
  )

  /**
   * Auto-select the first option when options change
   */
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false
      return // Skip execution on first mount to allow the form to be instanciated from csv
    }

    const optionValues = options.map(o => o.value)

    if (optionValues.length === 0) {
      field.onChange(undefined)
      if (onChange) {
        onChange()
      }
      return
    }

    // Preserve current if it remains valid
    if (optionValues.includes(field.value)) {
      return
    }

    // Otherwise choose the first valid option
    field.onChange(optionValues[0])
    if (onChange) {
      onChange()
    }
  }, [options, field.onChange, onChange])

  /**
   * Handle the change of the select
   */
  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    // Update the field value
    field.onChange(event.target.value)
    // Call the provided onChange function if it exists
    if (onChange) {
      onChange()
    }
  }

  const internalCss = css({
    /* font color when select is disabled */
    "select.bp5-disabled, select[disabled]": {
      color: "white !important",
    },
    /* Hide icon when select is disabled */
    ".bp5-html-select.bp5-disabled > .bp5-icon, .bp5-html-select > .bp5-icon.bp5-disabled, select:disabled + .bp5-icon":
      {
        display: "none !important",
      },
  })
  const mergedCss = css([internalCss, customCss])

  return (
    <WaveSelectContainer alignItems="center">
      <Select
        noDivWrapper={true}
        selectProps={{
          onChange: handleChange,
          options: options,
          value: options.length > 0 ? field.value || "" : "",
          fill: true,
          minimal: true,
          iconName: "caret-down",
          disabled: disabled || options.length === 0,
          ref: field.ref,
        }}
        customCss={mergedCss}
      />
      {fieldState.error && <ErrorIconWithTooltip errorMessage={fieldState.error.message} />}
    </WaveSelectContainer>
  )
}
